/*
 * CAPTURE-OWNED MODULE
 * How Template Studio draws and names a zone: its colors, its label pill
 * ("Invoice Number · anchor") and whether it is dimmed. Shared by the
 * interactive ZoneEditorOverlay and the read-only ReadOnlyZoneLayer so the
 * active and inactive pages look the same.
 */

export const ZONE_STYLES = {
  region: { bg: 'rgba(44, 111, 177, 0.08)', border: '#2C6FB1', label: 'Region' },
  anchor: { bg: 'rgba(245, 158, 11, 0.15)', border: '#f59e0b', label: 'Anchor' },
  field:  { bg: 'rgba(16, 185, 129, 0.18)', border: '#10b981', label: 'Field' },
  line_items_table: { bg: 'rgba(139, 92, 246, 0.14)', border: '#8b5cf6', label: 'Line Items Table' },
};

// A field zone holds the field's VALUE; "field" alone reads as the whole field.
export const ZONE_TYPE_NAMES = {
  field: 'value',
  anchor: 'anchor',
  region: 'region',
  line_items_table: 'table',
};

export function zoneTypeName(zoneType) {
  return ZONE_TYPE_NAMES[zoneType] || zoneType || 'zone';
}

/** "Invoice Number · anchor"; "Line Items Table" for a table zone. */
export function zoneLabel(zone, fields = []) {
  if (zone.zone_type === 'line_items_table') return 'Line Items Table';
  const field = fields.find(f => f.id === zone.field_id);
  const name = field?.label || field?.field_key || 'No field';
  return `${name} · ${zoneTypeName(zone.zone_type)}`;
}

export const DIMMED_OPACITY = 0.35;

/**
 * Anchor and region zones are dimmed unless "Show anchor and region" is on,
 * except the selected zone and the zones of the selected field.
 *
 * In a pick mode (Click a word, identifier pick) every zone is dimmed except
 * those of the field being placed (pickFieldId), whatever its type.
 */
export function isZoneDimmed(zone, { dimAnchorRegion, selectedZoneId = null, selectedFieldId = null, pickMode = false, pickFieldId = null }) {
  if (pickMode) return !(pickFieldId && zone.field_id === pickFieldId);
  if (!dimAnchorRegion) return false;
  if (zone.zone_type !== 'anchor' && zone.zone_type !== 'region') return false;
  if (zone.id === selectedZoneId) return false;
  if (selectedFieldId && zone.field_id === selectedFieldId) return false;
  return true;
}

/**
 * Whether a zone's label pill is shown. Value and table labels always are.
 * An anchor or region label sits on top of its field's value label, so it is
 * shown only while "Show anchor and region" is on, its field or the zone is
 * selected, or the zone is hovered.
 */
export function isZoneLabelShown(zone, { dimAnchorRegion, selectedZoneId = null, selectedFieldId = null, hoveredZoneId = null }) {
  if (zone.zone_type !== 'anchor' && zone.zone_type !== 'region') return true;
  if (zone.id === hoveredZoneId) return true;
  return !isZoneDimmed(zone, { dimAnchorRegion, selectedZoneId, selectedFieldId });
}

// Zone types drawn as an outline only: they cover much of the page, so their
// inside must not catch clicks meant for the zones and words within them.
export const OUTLINE_ZONE_TYPES = new Set(['line_items_table', 'region']);

export function isOutlineZone(zone) {
  return OUTLINE_ZONE_TYPES.has(zone.zone_type);
}

/**
 * Paint order for a page's zones: largest area first, so a smaller zone is
 * drawn on top of (and clicked before) any zone that contains it; the
 * selected zone last. Equal areas keep their incoming order.
 */
export function zonePaintOrder(zones, selectedZoneId = null) {
  return zones
    .map((zone, i) => ({ zone, i, area: (zone.w || 0) * (zone.h || 0) }))
    .sort((a, b) => {
      const sa = a.zone.id === selectedZoneId ? 1 : 0;
      const sb = b.zone.id === selectedZoneId ? 1 : 0;
      if (sa !== sb) return sa - sb;
      if (a.area !== b.area) return b.area - a.area;
      return a.i - b.i;
    })
    .map(({ zone }) => zone);
}
